/**
 * Helpers for structured past experience on EMT profiles.
 * Safe, deterministic; no NaN. Compatible with manager verification later.
 */
import type { EMTProfile, PastExperienceEntry, EMTShiftTypePreference } from "../types/firestore";

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

function parseDate(s: string | null | undefined): number | null {
  if (s == null || typeof s !== "string" || !s.trim()) return null;
  const t = Date.parse(s.trim());
  return Number.isFinite(t) ? t : null;
}

/**
 * Returns a safe array of past experience entries from profile. Never undefined.
 */
export function getSafePastExperience(
  profile: EMTProfile | null | undefined
): PastExperienceEntry[] {
  const raw = profile?.pastExperience;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is PastExperienceEntry =>
      e != null &&
      typeof e === "object" &&
      typeof e.id === "string" &&
      typeof e.employerName === "string" &&
      typeof e.roleTitle === "string"
  );
}

function formatShortDate(s: string): string {
  const d = parseDate(s);
  if (d == null) return "";
  const y = new Date(d).getFullYear();
  const m = new Date(d).getMonth() + 1;
  return Number.isFinite(y) ? (m >= 1 && m <= 12 ? `${m}/${y}` : String(y)) : "";
}

/** Safe verification label for public/employer display. No manager email or tokens. */
export type VerificationDisplayLabel =
  | "Self-reported"
  | "Pending manager review"
  | "Confirmed by manager";

function getVerificationLabel(status: PastExperienceEntry["verificationStatus"]): VerificationDisplayLabel {
  if (status === "manager_verified") return "Confirmed by manager";
  if (status === "pending_manager_review") return "Pending manager review";
  return "Self-reported";
}

/**
 * The stored experience label as shown to employers and on public profiles. publicProfiles written
 * before the server switched to "Confirmed by manager" (functions/src/readinessSync.ts) still hold
 * the legacy "Manager verified" until that profile is next recomputed; it is shown in today's wording.
 */
export function displayVerificationLabel(label: string): string {
  return label.trim().toLowerCase() === "manager verified" ? "Confirmed by manager" : label;
}

/** One entry for public/employer profile display (safe, concise). */
export type ExperienceSummaryDetailedItem = {
  employerName: string;
  roleTitle: string;
  serviceType: string;
  dateLabel: string;
  shiftTypesWorked?: EMTShiftTypePreference[];
  dutiesSummary?: string | null;
  accoladesText?: string | null;
  /** Safe label only; no tokens or manager email. */
  verificationLabel?: VerificationDisplayLabel;
  /** Verification of Employment hours, if the EMT uploaded a VOE document for this entry.
   *  Only the number is mirrored publicly — never the document URL itself. */
  voeHoursWorked?: number | null;
  /** Server-published (readinessSync, 2026-10-08): the private entry's id. */
  id?: string;
  /** Server-published: whole months in the role (null = dates not stated). See lib/employer/recruiterSignals.ts. */
  tenureMonths?: number | null;
  /** Server-published: an ENDED role under 6 months. */
  shortStint?: boolean;
  /** Server-published: the role is still held (tenure keeps counting after tenureAsOf). */
  currentlyWorking?: boolean;
  /** Server-published: UTC day tenureMonths was computed on. */
  tenureAsOf?: string;
};

/**
 * Build detailed experience summary for public profile sync. Safe, no private data.
 */
export function getExperienceSummaryDetailed(
  entries: PastExperienceEntry[]
): ExperienceSummaryDetailedItem[] {
  const safe = Array.isArray(entries) ? entries : [];
  return safe.map((e) => {
    let dateLabel = "";
    if (e.currentlyWorking && e.startDate) {
      dateLabel = `${formatShortDate(e.startDate)} – Present`;
    } else if (e.startDate || e.endDate) {
      dateLabel = [e.startDate, e.endDate]
        .filter((s): s is string => typeof s === "string" && s.length > 0)
        .map(formatShortDate)
        .join(" – ");
    }
    return {
      employerName: (e.employerName || "").trim() || "Employer",
      roleTitle: (e.roleTitle || "").trim() || "EMT",
      serviceType: (e.serviceType || "").trim() || "Other",
      ...(e.outsideEms === true ? { outsideEms: true } : {}),
      dateLabel,
      shiftTypesWorked: Array.isArray(e.shiftTypesWorked) && e.shiftTypesWorked.length > 0 ? e.shiftTypesWorked : undefined,
      dutiesSummary: (e.dutiesSummary || "").trim() || null,
      accoladesText: (e.accoladesText || "").trim() || null,
      verificationLabel: getVerificationLabel(e.verificationStatus),
      voeHoursWorked:
        typeof e.voeHoursWorked === "number" && Number.isFinite(e.voeHoursWorked) && e.voeHoursWorked > 0
          ? e.voeHoursWorked
          : undefined,
    };
  });
}

/**
 * Approximate total years from structured entries (from start/end or start–present).
 * Returns 0 if no valid entries; never NaN.
 */
export function getApproxExperienceYears(
  entries: PastExperienceEntry[],
  /**
   * Today as YYYY-MM-DD on the EMT's calendar (certUtils scoringToday). With it, "present" is that
   * day, so the total only changes at the EMT's midnight, like every other date rule in scoring
   * (2026-10-13). Without it, the current instant (older callers).
   */
  todayYmd?: string,
): number {
  const safe = Array.isArray(entries) ? entries : [];
  let totalMs = 0;
  const today = todayYmd && /^\d{4}-\d{2}-\d{2}$/.test(todayYmd) ? Date.parse(todayYmd) : NaN;
  const now = Number.isFinite(today) ? today : Date.now();
  for (const e of safe) {
    // EMS years stay EMS-only: a job outside EMS is work history, not EMS experience.
    if (e.outsideEms === true) continue;
    const start = parseDate(e.startDate);
    if (start == null) continue;
    let end: number;
    if (e.currentlyWorking) {
      end = now;
    } else {
      const endParsed = parseDate(e.endDate);
      end = endParsed != null ? endParsed : now;
    }
    if (end < start) continue;
    totalMs += end - start;
  }
  const years = totalMs / MS_PER_YEAR;
  if (!Number.isFinite(years) || Number.isNaN(years)) return 0;
  return Math.max(0, Math.round(years * 10) / 10);
}
